/**
 * Trace ID Utility
 * Handles generation and management of trace IDs for request tracing
 * and distributed logging across microservices
 */

/**
 * Header name for trace ID propagation
 */
export const TRACE_ID_HEADER = 'X-Trace-Id';

/**
 * Storage key for current trace ID
 */
const TRACE_ID_STORAGE_KEY = 'current-trace-id';

/**
 * Generate a unique trace ID using timestamp and random values
 * Format: {timestamp}-{random}
 * @returns A unique trace ID string
 */
export const generateTraceId = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
};

/**
 * Get the current trace ID from session storage
 * If not present, generates a new one
 * @returns The current trace ID
 */
export const getTraceId = (): string => {
  if (typeof window === 'undefined') {
    return generateTraceId();
  }

  try {
    let traceId = sessionStorage.getItem(TRACE_ID_STORAGE_KEY);
    if (!traceId) {
      traceId = generateTraceId();
      sessionStorage.setItem(TRACE_ID_STORAGE_KEY, traceId);
    }
    return traceId;
  } catch (error) {
    // Fallback if sessionStorage is not available
    return generateTraceId();
  }
};

/**
 * Set a new trace ID (typically from incoming request headers)
 * @param traceId - The trace ID to set
 */
export const setTraceId = (traceId: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(TRACE_ID_STORAGE_KEY, traceId);
  } catch (error) {
    console.warn('[traceId] Failed to set trace ID in sessionStorage:', error);
  }
};

/**
 * Clear the current trace ID (useful for testing or session cleanup)
 */
export const clearTraceId = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(TRACE_ID_STORAGE_KEY);
  } catch (error) {
    console.warn(
      '[traceId] Failed to clear trace ID from sessionStorage:',
      error
    );
  }
};
